import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { getDashboardPath } from '../lib/auth';
import { Baby, Mail, Lock, User, AlertCircle, Building2, Phone, ArrowLeft, Gift } from 'lucide-react';

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated, user, isLoading } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    clinicName: '',
    phone: '',
    kvkkConsent: false,
  });

  React.useEffect(() => {
    if (isAuthenticated && user) {
      navigate(getDashboardPath(user.role), { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F7FC]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5A67D8]"></div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) return setError('Ad soyad gerekli');
    if (!formData.email.trim()) return setError('E-posta gerekli');
    if (!formData.password) return setError('Şifre gerekli');
    if (formData.password.length < 6) return setError('Şifre en az 6 karakter olmalı');
    if (formData.password !== formData.confirmPassword) return setError('Şifreler eşleşmiyor');
    if (!formData.clinicName.trim()) return setError('Klinik adı gerekli');
    if (!formData.kvkkConsent) return setError('Devam etmek için KVKK aydınlatma metnini onaylamanız gerekir.');

    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name, {
        clinicName: formData.clinicName,
        phone: formData.phone,
        kvkkConsent: true,
      });
    } catch (err) {
      let msg = String(err?.message || 'Kayıt olurken bir hata oluştu');
      if (msg.toLowerCase().indexOf('cloned') !== -1 || err?.name === 'DataCloneError') {
        msg = 'Kayıt olurken bir hata oluştu. Lütfen tekrar deneyin.';
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F7FC] to-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Link
          to="/"
          className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Ana Sayfaya Dön
        </Link>

        <Card className="border-0 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5A67D8] to-[#4338CA] flex items-center justify-center mx-auto mb-4">
              <Baby className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Ücretsiz Hesap Oluştur
            </CardTitle>
            <CardDescription className="text-slate-500">
              Hemen başlayın — 1 ücretsiz deneme hakkı hediye
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {/* Free starter badge */}
            <div className="mb-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-700">
              <Gift className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium">Ücretsiz Başlangıç: 1 adet 4K ultrason üretim hakkı hediye!</span>
            </div>

            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Ad Soyad *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="name" name="name" placeholder="Dr. Adınız Soyadınız" value={formData.name} onChange={handleInputChange} className="pl-10" data-testid="register-name" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="phone" name="phone" placeholder="0532 123 45 67" value={formData.phone} onChange={handleInputChange} className="pl-10" data-testid="register-phone" />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clinicName">Klinik Adı *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="clinicName" name="clinicName" placeholder="Örn: ABC Kadın Doğum Kliniği" value={formData.clinicName} onChange={handleInputChange} className="pl-10" data-testid="register-clinic" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">E-posta *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="email" name="email" type="email" placeholder="doktor@klinik.com" value={formData.email} onChange={handleInputChange} className="pl-10" data-testid="register-email" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Şifre *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="password" name="password" type="password" placeholder="Min. 6 karakter" value={formData.password} onChange={handleInputChange} className="pl-10" data-testid="register-password" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Şifre Tekrar *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="confirmPassword" name="confirmPassword" type="password" placeholder="Tekrar girin" value={formData.confirmPassword} onChange={handleInputChange} className="pl-10" data-testid="register-confirm-password" />
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer mt-2" data-testid="register-kvkk-label">
                <input
                  type="checkbox"
                  name="kvkkConsent"
                  checked={formData.kvkkConsent}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, kvkkConsent: e.target.checked }))
                  }
                  className="mt-0.5 w-4 h-4"
                  data-testid="register-kvkk-checkbox"
                />
                <span className="text-xs text-amber-900 leading-relaxed">
                  <strong>KVKK Aydınlatma Metni</strong>'ni okudum ve kişisel verilerimin
                  yukarıdaki şekilde işlenmesini onaylıyorum.{' '}
                  <Link
                    to="/kvkk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-amber-700"
                  >
                    Metni görüntüle
                  </Link>
                </span>
              </label>

              <Button
                type="submit"
                disabled={loading || !formData.kvkkConsent}
                className="w-full bg-[#5A67D8] hover:bg-[#4338CA] text-white h-12 mt-4 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Kaydediliyor...
                  </div>
                ) : (
                  'Ücretsiz Hesap Oluştur'
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-500 mt-6">
              Zaten hesabınız var mı?{' '}
              <Link to="/login" className="text-[#5A67D8] hover:underline font-medium">
                Giriş Yap
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
